// Capacitor Imports
import { Channel, FirebaseMessaging, GetTokenOptions, Importance, Visibility } from '@capacitor-firebase/messaging';
import { Capacitor } from "@capacitor/core";

// Angular Imports
import { Injectable, NgZone } from '@angular/core';
import { Router } from "@angular/router";
import { getMessaging, onMessage } from '@angular/fire/messaging';

// 3rd Party Imports

// Custom Services
import { UserService } from "@app/services/user.service";
import { DebugService } from './debug.service';

// Application Environment
import { environment } from "@env/environment";

/**
 * Represents the FMCM service Class
 * @export
 * @class FcmServiceService
 * @typedef {FcmServiceService}
 */
@Injectable({
    providedIn: 'root'
})
export class FcmServiceService {

    /**
     * @description Check if the service is initialized
     * @type {boolean} - true if the service is initialized, false otherwise
     * @defaultvalue false
     */
    private isInitialized = false;

    /**
     * @description FCM token
     * @type {string} - FCM token
     * @defaultvalue ""
     */
    public token = "";

    /**
     * Reference to the FCM (Firebase Cloud Messaging) service
     * @class
     * @param {DebugService} debugService - Reference to the debug service
     * @param {NgZone} ngZone - Reference to the NgZone service
     * @param {Router} router - Reference to the router service
     * @param {UserService} userSerivce - Reference to the user service    
     */
    constructor(
        private debugService: DebugService,
        private ngZone: NgZone,
        private router: Router,
        private userSerivce: UserService
    ) {

        if (this.isInitialized) {
            return;
        }

        this.initFcmService();
    } // End of constructor()

    /**
     * @function - initFcmService
     * @description - Initialize the FCM service
     * @returns {Promise<void>}
     */
    public async initFcmService(): Promise<void> {

        await FirebaseMessaging.removeAllListeners();

        FirebaseMessaging.addListener('tokenReceived', (event) => {
            this.debugService.debug(`tokenReceived, saving...`, { event });
            this.userSerivce.saveFcmTokenToFirestore(event.token);
            // @TODO: save token somewhere
            this.ngZone.run(() => {
                this.token = event.token;
            });
        });

        FirebaseMessaging.addListener("notificationReceived", (event) => {
            // triggers in PWA when the app is in the foreground
            this.debugService.debug("notificationReceived: ", { event });
        });
        FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
            this.debugService.debug("notificationActionPerformed: ", { event });
        });

        if (Capacitor.getPlatform() === "web" && Capacitor.isNativePlatform() === false) {

            this.debugService.debug('PWA: initFcmService() running on web platform');
            // check if service worker is supported
            if ("serviceWorker" in navigator && navigator.serviceWorker !== null) {
                this.debugService.debug('PWA: initFcmService() service worker is supported');

                // add event listener to check for incoming notifications from the service worker
                const messaging = getMessaging();
                onMessage(messaging, (payload) => {
                    console.log('Message received. ', payload);
                    // ...
                });




                // navigator.serviceWorker.addEventListener("message", (event: any) => {

                //     this.logger.debug('PWA: initFcmService() service worker message received: ', event);
                //     // Check if the message is a notification
                //     const incomingNotification = event.data.notification;
                //     if (!incomingNotification) {
                //         return;
                //     }

                //     // Check if Notifications are available, permissions are granted, if denied, do nothing
                //     if (!("Notification" in window)) {
                //         // Check if the browser supports notifications
                //         return;
                //     } else if (Notification.permission === "granted") {
                //         const notification = new Notification(incomingNotification.title, { body: incomingNotification.body });
                //         notification.onclick = (event) => {
                //             // Handle click on the notification
                //             this.router.navigateByUrl('/app/notifications');
                //         };
                //     } else if (Notification.permission !== "denied") {
                //         Notification.requestPermission().then(permission => {
                //             if (permission === "granted") {
                //                 this.logger.debug("Notification permission granted");
                //                 const notification = new Notification(incomingNotification.title, { body: incomingNotification.body });
                //                 notification.onclick = (event) => {
                //                     // Handle click on the notification
                //                     this.router.navigateByUrl('/app/notifications');
                //                 };
                //             }
                //         }).catch((error) => { });
                //     } else {
                //         this.logger.debug("Notification permission denied");
                //     } // End of if (Notification.permission === "granted")

                // }); // End of navigator.serviceWorker.addEventListener("message", (event: any) =>

            } // End of if ("serviceWorker" in navigator && navigator.serviceWorker !== null)

        } // End of if (Capacitor.getPlatform() === "web")

        this.isInitialized = true;

    } // End of initFcmService()

    /**
     * @function - checkPermissions
     * @description Check if the user has granted permission to receive push notifications
     * @returns {Promise<string>}
     */
    public async checkPermissions(): Promise<string> {

        try {
            const result = await FirebaseMessaging.checkPermissions();
            return result.receive;
        } catch (error) {
            this.debugService.error("checkPermissions() error should not happen: ", error);
        }

        return 'prompt'; // should not reach here
    } // End of checkPermissions()


    /**
     * @function - requestPermissions
     * @description Request permission to receive push notifications
     * @returns {Promise<string>}
     */
    public async requestPermissions(): Promise<string> {

        try {
            const result = await FirebaseMessaging.requestPermissions();
            return result.receive;
        } catch (error) {
            this.debugService.error("requestPermissions() error should not happen: ", error);
        }

        return 'prompt'; // should not reach here
    } // End of requestPermissions()

    /**
     * @function - getToken
     * @description Get the FCM token
     * @returns {Promise<void>}
     */
    public async getToken(): Promise<void> {

        // If permissions are explicitly denied, don't request a token
        const currentPermissions = await this.checkPermissions();
        if (currentPermissions === "denied") {
            this.debugService.debug("getToken() permissions denied in OS, not doing anything");
            return;
        }

        // Create options object
        const options: GetTokenOptions = {
            vapidKey: environment.firebase.vapidKey,
        };
        if (Capacitor.getPlatform() === "web") {
            options.serviceWorkerRegistration = await navigator.serviceWorker.register("/assets/js/push/fcm/firebase-messaging-sw.js", { 
                scope: '/assets/js/push/fcm/',
                type: 'module' });
        }

        // this.logger.debug("getToken() requesting token with options: ", options);
        // FirebaseMessaging.getToken(options).then(({ token }) => {
        //     this.logger.debug("getToken() token received: ", token);
        //     this.token = token;
        //     this.userSerivce.saveFcmTokenToFirestore(this.token);
        // }).catch((error) => {
        //     this.logger.error("getToken() error should not happen: ", error);
        // });

        // if the token request prompt is blocked, catch the exception and do nothing
        try {
            const { token } = await FirebaseMessaging.getToken(options);
            this.userSerivce.saveFcmTokenToFirestore(token);
            this.token = token;
        } catch (error) {
            this.debugService.error("getToken() error should not happen: ", error);
        }

    } // End of getToken()

    /**
     * @function - deleteToken
    *  @description Delete the FCM token and unregister the device with Firebase
     * @returns {Promise<void>}
     */
    public async deleteToken(): Promise<void> {

        // If no token is set, don't try to delete it, it will trigger an exception
        if (this.token === '') {
            return;
        }

        // Unregister and delete theh token.
        FirebaseMessaging.deleteToken().then(async () => {
            this.token = ''; // delete first locally, then delete from Firestore
            this.userSerivce.deleteFcmTokenFromFirestore().then(() => {
            }).catch((error) => {
                this.debugService.error("deleteToken() error deleting token from Firestore: ", error);
            });
        }).catch((error) => {
            this.debugService.error("deleteToken() error from FCM: ", error);
        });

    } // End of deleteToken()

    /**
     * @function - getDeliveredNotifications
     * @description - Get delivered Notifications
     * @returns {Promise<void>}
     */
    public async getDeliveredNotifications(): Promise<void> {
        const result = await FirebaseMessaging.getDeliveredNotifications();
        // this.deliveredNotifications = result.notifications;
    }

    /**
     * @function - removeAllDeliveredNotifications
     * @description - Remove all delivered Notifications
     * @returns {Promise<void>}
     */
    public async removeAllDeliveredNotifications(): Promise<void> {
        await FirebaseMessaging.removeAllDeliveredNotifications();
        await this.getDeliveredNotifications();
    }

    /**
     * @function - removeDeliveredNotifications
     * @param {Notification} notification
     * @returns {Promise<void>}
     */
    public async removeDeliveredNotifications(notification: Notification): Promise<void> {
        await FirebaseMessaging.removeDeliveredNotifications({
            notifications: [notification],
        });
        await this.getDeliveredNotifications();
    }

} // End of class FcmServiceService